import React, { lazy } from "react";
import { Route } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";

import { ROIDashboardPage } from "@/modules/stochastic/features/ROIDashboard/components/ROIDashboardPage/ROIDashboardPage";
import DraftInvoiceCalculator from "../../modules/stochastic/features/CampaignBillingManagement/components/DraftInvoiceCalculator/DraftInvoiceCalculator";
import TotalAddressableMarketPage from "@/modules/stochastic/features/TotalAddressableMarket/components/TotalAddressableMarketPage/TotalAddressableMarketPage";

const DashboardPage = lazy(() => import("@/modules/stochastic/features/SalesAndCustomerDashboard/components/DashboardPage/DashboardPage"));
const CustomerList = lazy(() => import("../../modules/stochastic/features/CustomerList/components/CustomerList/CustomerList"));
const InvoiceList = lazy(() => import("../../modules/stochastic/features/InvoiceList/components/InvoiceList/InvoiceList"));
const ProspectList = lazy(() => import("../../modules/stochastic/features/ProspectList/components/ProspectList/ProspectList"));
const DoNotMailList = lazy(() => import("@/modules/stochastic/features/DoNotMailManagement/components/DoNotMailList/DoNotMailList"));
const CampaignList = lazy(() => import("../../modules/stochastic/features/CampaignManagement/components/CampaignList/CampaignList"));
const BatchProspectList = lazy(() => import("../../modules/stochastic/features/BatchProspectManagement/components/BatchProspectList/BatchProspectList"));
const CampaignBatchList = lazy(() => import("../../modules/stochastic/features/CampaignBatchManagement/components/CampaignBatchList/CampaignBatchList"));
const CampaignBillingList = lazy(() => import("../../modules/stochastic/features/CampaignBillingManagement/components/CampaignBillingList/CampaignBillingList"));
const CampaignFilesList = lazy(() => import("../../modules/stochastic/features/CampaignFileManagement/components/CampaignFilesList/CampaignFilesList"));
const FieldServiceImport = lazy(() => import("@/modules/stochastic/features/FieldServiceImport/components/FieldServiceImport/FieldServiceImport").then(module => ({ default: module.FieldServiceImport })));
const ProspectSourceImport = lazy(() => import("@/modules/stochastic/features/ProspectSourceImport/components/ProspectSourceImport/ProspectSourceImport").then(module => ({ default: module.ProspectSourceImport })));
const ImportStatus = lazy(() => import("@/modules/stochastic/features/ImportStatus/components/ImportStatus/ImportStatus"));
const CreateCampaignPage = lazy(() => import("@/modules/stochastic/features/CampaignManagement/components/CreateCampaignPage/CreateCampaignPage"));
const ViewCampaignDetailsPage = lazy(() => import("@/modules/stochastic/features/CampaignManagement/components/ViewCampaignDetailsPage/ViewCampaignDetailsPage"));
const CloneCampaignPage = lazy(() => import("@/modules/stochastic/features/CampaignManagement/components/CloneCampaign/CloneCampaignPage"));
const CampaignProductList = lazy(() => import("@/modules/stochastic/features/CampaignProductManagement/components/CampaignProductList/CampaignProductList"));
const PostageExpenseImport = lazy(() => import("@/modules/stochastic/features/PostageExpenseImport/components/PostageExpenseImport/PostageExpenseImport").then(module => ({ default: module.PostageExpenseImport })));
const LocationsList = lazy(() => import("@/modules/stochastic/features/LocationsList/components/LocationList/LocationsList"));
const DoNotMailListImport = lazy(() => import("@/modules/stochastic/features/DoNotMailImport/components/DoNotMailListImport/DoNotMailListImport").then(module => ({ default: module.DoNotMailListImport })));
const CampaignStatusPage = lazy(() => import("@/modules/administration/features/Stochastic/CampaignStatus/components/CampaignStatusPage/CampaignStatusPage"));
const CampaignJobStatusPage = lazy(() => import("@/modules/administration/features/Stochastic/CampaignJobStatus/components/CampaignJobStatusPage/CampaignJobStatusPage"));
const MarketingPage = lazy(() => import("@/modules/stochastic/features/MarketingPage/components/MarketingPage/MarketingPage"));

const StochasticMainLayout = () => <MainLayout section="stochastic" />;

const StochasticRoutes = (
  <Route
    element={<AuthenticationGuard component={StochasticMainLayout} />}
    path="/lead-generation"
  >
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-dashboard"]}>
          <DashboardPage />
        </PermissionGuard>
      }
      path="sales-and-customer-dashboard"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-roi-dashboard"]}>
          <ROIDashboardPage />
        </PermissionGuard>
      }
      path="roi-dashboard"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-customers"]}>
          <CustomerList />
        </PermissionGuard>
      }
      path="customers"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-invoices"]}>
          <InvoiceList />
        </PermissionGuard>
      }
      path="invoices"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <DraftInvoiceCalculator />
        </PermissionGuard>
      }
      path="draft-invoice"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-prospects"]}>
          <ProspectList />
        </PermissionGuard>
      }
      path="prospects"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-locations"]}>
          <LocationsList />
        </PermissionGuard>
      }
      path="locations"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-total-addressable-market"]}
        >
          <TotalAddressableMarketPage />
        </PermissionGuard>
      }
      path="total-addressable-market"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <DoNotMailList />
        </PermissionGuard>
      }
      path="do-not-mail"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CampaignList />
        </PermissionGuard>
      }
      path="campaigns"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignStatusPage />
        </PermissionGuard>
      }
      path="campaign-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignJobStatusPage />
        </PermissionGuard>
      }
      path="campaign-job-status"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignBillingList />
        </PermissionGuard>
      }
      path="campaigns/billing"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CampaignBatchList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/batches"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <BatchProspectList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/batches/:batchId/prospects"
    />

    {/* Files, FieldServiceImport, ProspectSourceImport require stochastic-uploads permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignFilesList />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/files"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <FieldServiceImport />
        </PermissionGuard>
      }
      path="field-service-import"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <ProspectSourceImport />
        </PermissionGuard>
      }
      path="prospect-source-import"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <DoNotMailListImport />
        </PermissionGuard>
      }
      path="do-not-mail-import"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["stochastic-uploads"]}>
          <ImportStatus />
        </PermissionGuard>
      }
      path="import-status"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CreateCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/new"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <ViewCampaignDetailsPage />
        </PermissionGuard>
      }
      path="campaigns/:campaignId/view"
    />

    <Route
      element={
        <PermissionGuard
          requiredPermissions={["stochastic-campaign-management"]}
        >
          <CloneCampaignPage />
        </PermissionGuard>
      }
      path="campaigns/:id/clone"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <CampaignProductList />
        </PermissionGuard>
      }
      path="products/campaign"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["administration-stochastic"]}>
          <PostageExpenseImport />
        </PermissionGuard>
      }
      path="campaigns/postage"
    />

    <Route element={<MarketingPage />} path="marketing" />
  </Route>
);

export default StochasticRoutes;
